import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Logo, Spinner } from "@/components/Shared";

export const AuthShell = ({ children, title, sub }) => (
  <div className="flex min-h-screen flex-col bg-white lg:flex-row">
    <div className="relative hidden w-[44%] flex-col justify-between overflow-hidden bg-[#5C241E] p-12 text-white lg:flex">
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "repeating-linear-gradient(135deg,#fff 0 2px,transparent 2px 22px)" }} />
      <p className="relative text-xs font-bold uppercase tracking-[0.25em] text-white/70">QDB Consulting LLC</p>
      <div className="relative">
        <h2 className="font-display text-5xl font-bold leading-[1.05]">Inspectors Daily<br />Progress Reports</h2>
        <p className="mt-4 max-w-sm text-white/75">One place for every jobsite report, footage count and photo — submitted from the field, ready for the office.</p>
      </div>
      <p className="relative text-xs text-white/50">Authorized personnel only</p>
    </div>
    <div className="flex flex-1 items-center justify-center px-5 py-10">
      <div className="w-full max-w-sm fade-up">
        <Logo className="mb-8 h-20" />
        <h1 className="text-3xl font-bold text-slate-900">{title}</h1>
        {sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}
        <div className="mt-7">{children}</div>
      </div>
    </div>
    <a href="https://builtby-ab.com" target="_blank" rel="noopener noreferrer" data-testid="built-by-ab-link" className="fixed bottom-3 right-4 text-[11px] font-medium text-slate-600 transition-colors hover:text-[#5C241E]">built by ab</a>
  </div>
);

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(email, password, remember);
      nav(u.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Sign in" sub="Field reporting portal">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-slate-800" htmlFor="email">Email</label>
          <input id="email" type="email" autoComplete="email" required className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-slate-800" htmlFor="password">Password</label>
          <div className="relative">
            <input id="password" type={show ? "text" : "password"} autoComplete="current-password" required className="field-input pr-12" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-1 top-1 flex h-10 w-10 items-center justify-center text-slate-500" data-testid="login-toggle-password" aria-label="Show password">
              {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-5 w-5 accent-[#5C241E]" data-testid="login-remember-checkbox" />
            Keep me signed in
          </label>
          <Link to="/forgot-password" className="text-sm font-semibold text-[#5C241E] hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
        </div>
        {error && <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700" data-testid="login-error">{error}</p>}
        <button type="submit" disabled={busy} className="btn-brand h-14 w-full text-base" data-testid="login-submit-button">
          {busy ? <Spinner className="text-white" /> : <Lock className="h-4 w-4" />} Sign in
        </button>
        <p className="text-center text-sm"><Link to="/privacy" className="font-semibold text-[#5C241E] underline-offset-2 hover:underline" data-testid="login-privacy-link">Employee privacy notice</Link></p>
      </form>
    </AuthShell>
  );
}
